import { localStorageService } from '../common/service/local-storage-service'
import { HOME_APP_TOUR_ONBOARDING_STEP, useHomeOnboardingAppTourGate } from '../context/home-onboarding-app-tour-gate-context'
import { useSessionContext } from '../context/session-context'
import { useMediaQuery } from '../hook/use-media-query'
import { useUpdateUsername } from '../hook/use-update-username'
import { usernameSchema, type UsernameFormValues } from './shared'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog'
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from './ui/drawer'
import { Field } from './ui/field'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { cn } from '../lib/utils'

const headerClassName = 'flex flex-col gap-5 items-center text-center'
const titleClassName = 'font-evogria text-3xl font-normal leading-10 text-ivory'
const descriptionClassName = 'font-roboto-condensed text-base text-mist leading-6 tracking-[0.5px]'

export const PickUsernameModal = (): React.ReactElement => {
  const { me } = useSessionContext()
  const { notifyStepComplete } = useHomeOnboardingAppTourGate()
  const isMobile = useMediaQuery('(max-width: 552px)')
  const open = !!me && !localStorageService.getPickUsernameModalSeen()
  const initialUsername = me?.profile?.username

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { isValid },
  } = useForm<UsernameFormValues>({
    resolver: yupResolver(usernameSchema),
    defaultValues: {
      username: initialUsername || '',
    },
    mode: 'onChange',
  })

  const { updateUsername, isUpdating: isPending } = useUpdateUsername({
    onSuccess: () => {
      localStorageService.setPickUsernameModalSeen()
      notifyStepComplete(HOME_APP_TOUR_ONBOARDING_STEP.PICK_USERNAME)
      reset()
    },
    onError: (field, { type, message }) => {
      setError(field, { type, message })
    },
  })

  useEffect(() => {
    if (open) {
      reset({
        username: initialUsername || '',
      })
    }
  }, [open, initialUsername, reset])

  const onSubmit = (data: UsernameFormValues): void => {
    updateUsername(data.username)
  }

  const isSaveDisabled = !isValid || isPending

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 items-center">
      <div className="w-full flex flex-col gap-1">
        <Controller
          control={control}
          name="username"
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <Field
              label="Username"
              hideLabel
              name="username"
              value={value ?? ''}
              placeholder="Enter your username"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
              onBlur={onBlur}
              error={error?.message}
              disabled={isPending}
              inputClassName="font-roboto-condensed-medium"
              autoFocus
            />
          )}
        />
      </div>
      <Button type="submit" variant="default" isLoading={isPending} disabled={isSaveDisabled}>
        SAVE
      </Button>
    </form>
  )

  if (isMobile) {
    return (
      <Drawer open={open} dismissible={false}>
        <DrawerContent className="bg-primary-gray p-8 rounded-t-md">
          <DrawerHeader className={headerClassName}>
            <DrawerTitle className={titleClassName}>
              PICK YOUR <span className="font-evogria text-primary">USERNAME</span>
            </DrawerTitle>
            <DrawerDescription className={descriptionClassName}>
              We&apos;ve suggested something for you - feel free to keep it or type whatever feels right. No pressure, you can
              always change it later.
            </DrawerDescription>
          </DrawerHeader>
          {formContent}
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open}>
      <DialogContent
        className={cn('sm:max-w-md [&>button]:hidden')}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className={headerClassName}>
          <DialogTitle className={titleClassName}>
            PICK YOUR <span className="font-evogria text-primary">USERNAME</span>
          </DialogTitle>
          <DialogDescription className={descriptionClassName}>
            We&apos;ve suggested something for you - feel free to keep it or type whatever feels right. No pressure, you can
            always change it later.
          </DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  )
}
